import { useState, useEffect } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  Settings,
  Upload,
  Palette,
  Users,
  CheckCircle2,
  Crown,
  MapPin,
  Calendar,
  Edit3,
  Save,
  Image,
  Globe,
  Lock,
  Unlock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color presets
const COLOR_PRESETS = [
  { name: 'Vert Agricole', primary: '#22c55e', secondary: '#166534' },
  { name: 'Bleu Vigne', primary: '#3b82f6', secondary: '#1e40af' },
  { name: 'Violet Lavande', primary: '#8b5cf6', secondary: '#5b21b6' },
  { name: 'Orange Récolte', primary: '#f59e0b', secondary: '#b45309' },
  { name: 'Rouge Cerise', primary: '#ef4444', secondary: '#b91c1c' },
  { name: 'Terre', primary: '#78716c', secondary: '#44403c' },
];

const WorkspacePage = () => {
  const { tenant, societes, activeSociete, switchSociete, membership, refreshWorkspace } = useWorkspace();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    nom_affiche: '',
    logo_url: '',
    couleur_primaire: '#22c55e',
    couleur_secondaire: '#166534',
  });

  // Initialize branding from tenant
  useEffect(() => {
    if (tenant?.branding) {
      setBranding({
        nom_affiche: tenant.branding.nom_affiche || tenant.nom,
        logo_url: tenant.branding.logo_url || '',
        couleur_primaire: tenant.branding.couleur_primaire || '#22c55e',
        couleur_secondaire: tenant.branding.couleur_secondaire || '#166534',
      });
    }
  }, [tenant]);

  // Save branding
  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenant/${tenant.id}/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(branding)
      });

      if (response.ok) {
        toast.success('Personnalisation enregistrée');
        setEditing(false);
        refreshWorkspace();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, create a data URL (in production, upload to storage)
    const reader = new FileReader();
    reader.onloadend = () => {
      setBranding(prev => ({ ...prev, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  if (!tenant) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="no-workspace">
        <Card className="max-w-md mx-auto text-center p-8">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Aucun workspace configuré</h2>
          <p className="text-muted-foreground mb-4">
            Utilisez le Creator pour configurer votre premier workspace
          </p>
          <Button onClick={() => window.location.href = '/creator'}>
            Créer mon workspace
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workspace-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt="Logo" 
              className="w-16 h-16 rounded-xl object-cover border"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: branding.couleur_primaire + '20' }}
            >
              <Building2 className="w-8 h-8" style={{ color: branding.couleur_primaire }} />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{branding.nom_affiche || tenant.nom}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary">{tenant.type_tenant}</Badge>
              <Badge variant="outline" className="capitalize">
                <Crown className="w-3 h-3 mr-1" />
                {membership?.role}
              </Badge>
            </div>
          </div>
        </div>

        <Button 
          variant={editing ? "default" : "outline"}
          onClick={() => editing ? handleSaveBranding() : setEditing(true)}
          disabled={saving}
          data-testid="edit-branding-btn"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          ) : editing ? (
            <Save className="w-4 h-4 mr-2" />
          ) : (
            <Edit3 className="w-4 h-4 mr-2" />
          )}
          {editing ? 'Enregistrer' : 'Personnaliser'}
        </Button>
      </div>

      <Tabs defaultValue="societes" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="societes" data-testid="tab-societes">
            <Building2 className="w-4 h-4 mr-2" />
            Sociétés ({societes.length})
          </TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">
            <Palette className="w-4 h-4 mr-2" />
            Personnalisation
          </TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access">
            <Users className="w-4 h-4 mr-2" />
            Accès Client
          </TabsTrigger>
        </TabsList>

        {/* Sociétés Tab */}
        <TabsContent value="societes" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {societes.map((societe) => (
              <Card 
                key={societe.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  activeSociete?.id === societe.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => switchSociete(societe.id)}
                data-testid={`societe-card-${societe.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{societe.nom}</h3>
                        {societe.est_societe_pivot && (
                          <Badge variant="default" className="text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            Pivot
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="mt-1">
                        {societe.forme_juridique}
                      </Badge>
                    </div>
                    {activeSociete?.id === societe.id && (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    )}
                  </div>

                  {societe.ville && (
                    <div className="flex items-center gap-1 mt-3 text-sm text-muted-foreground">
                      <MapPin className="w-3 h-3" />
                      {societe.ville}
                    </div>
                  )}

                  {societe.type_activite?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {societe.type_activite.slice(0, 3).map((act, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {act}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {societes.length === 0 && (
            <Card className="text-center p-8">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune société configurée</p>
            </Card>
          )}
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Logo & Identité</CardTitle>
              <CardDescription>
                Personnalisez l'apparence de votre workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  {branding.logo_url ? (
                    <img 
                      src={branding.logo_url} 
                      alt="Logo" 
                      className="w-24 h-24 rounded-xl object-cover border"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center">
                      <Image className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={!editing}
                      className="max-w-xs"
                      data-testid="logo-upload"
                    />
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG jusqu'à 2MB. Recommandé: 200x200px
                    </p>
                  </div>
                </div>
              </div>

              {/* Nom affiché */}
              <div className="space-y-2">
                <Label>Nom affiché</Label>
                <Input
                  value={branding.nom_affiche}
                  onChange={(e) => setBranding(prev => ({ ...prev, nom_affiche: e.target.value }))}
                  disabled={!editing}
                  placeholder="Nom de votre entreprise"
                  data-testid="nom-affiche-input"
                />
              </div>

              {/* Couleurs */}
              <div className="space-y-4">
                <Label>Couleurs</Label>
                
                {/* Presets */}
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => editing && setBranding(prev => ({
                        ...prev,
                        couleur_primaire: preset.primary,
                        couleur_secondaire: preset.secondary
                      }))}
                      disabled={!editing}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                        branding.couleur_primaire === preset.primary
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-border hover:border-muted-foreground/50'
                      } ${!editing ? 'opacity-50' : ''}`}
                      data-testid={`color-preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div 
                        className="w-6 h-6 rounded-full"
                        style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }}
                      />
                      <span className="text-sm">{preset.name}</span>
                    </button>
                  ))}
                </div>

                {/* Custom colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Couleur primaire</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={branding.couleur_primaire}
                        onChange={(e) => setBranding(prev => ({ ...prev, couleur_primaire: e.target.value }))}
                        disabled={!editing}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.couleur_primaire}
                        onChange={(e) => setBranding(prev => ({ ...prev, couleur_primaire: e.target.value }))}
                        disabled={!editing}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Couleur secondaire</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={branding.couleur_secondaire}
                        onChange={(e) => setBranding(prev => ({ ...prev, couleur_secondaire: e.target.value }))}
                        disabled={!editing}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={branding.couleur_secondaire}
                        onChange={(e) => setBranding(prev => ({ ...prev, couleur_secondaire: e.target.value }))}
                        disabled={!editing}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 rounded-xl border bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-3">Aperçu</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: branding.couleur_primaire }}
                    >
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: branding.couleur_primaire }}>
                        {branding.nom_affiche || 'Mon Entreprise'}
                      </p>
                      <p className="text-sm" style={{ color: branding.couleur_secondaire }}>
                        Bureau Agricole Numérique
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Tab */}
        <TabsContent value="access" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Accès Client</CardTitle>
              <CardDescription>
                Informations de connexion pour vos clients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* URL d'accès */}
              <div className="space-y-2">
                <Label>URL d'accès</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-muted rounded-lg">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <code className="text-sm">
                      {window.location.origin}/workspace/{tenant.slug}
                    </code>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/workspace/${tenant.slug}`);
                      toast.success('URL copiée');
                    }}
                  >
                    Copier
                  </Button>
                </div>
              </div>

              {/* Statut */}
              <div className="space-y-2">
                <Label>Statut de l'abonnement</Label>
                <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <Unlock className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800 dark:text-green-400">Actif</p>
                    <p className="text-sm text-green-700 dark:text-green-500">
                      {tenant.date_expiration 
                        ? `Expire le ${new Date(tenant.date_expiration).toLocaleDateString('fr-FR')}`
                        : 'Pas de date d\'expiration'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{societes.length}</p>
                  <p className="text-sm text-muted-foreground">Sociétés</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{tenant.nb_utilisateurs || 1}</p>
                  <p className="text-sm text-muted-foreground">Utilisateurs</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-2xl font-bold capitalize">{tenant.type_tenant}</p>
                  <p className="text-sm text-muted-foreground">Plan</p>
                </div>
              </div>

              {/* Modules actifs */}
              {tenant.config && (
                <div className="space-y-2">
                  <Label>Modules actifs</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(tenant.config)
                      .filter(([_, active]) => active)
                      .map(([module]) => (
                        <Badge key={module} variant="secondary" className="capitalize">
                          {module.replace('_', ' ')}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkspacePage;
